import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Activity, AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle, ListChecks } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_META = {
  green: {
    label: 'Healthy',
    bg: 'bg-gradient-to-br from-[#07743F] to-[#0a8f4d]',
    text: 'text-white',
    Icon: CheckCircle2,
  },
  amber: {
    label: 'Partial',
    bg: 'bg-gradient-to-br from-[#E4E517] to-[#f3f25a]',
    text: 'text-gray-900',
    Icon: AlertTriangle,
  },
  red: {
    label: 'Action Required',
    bg: 'bg-gradient-to-br from-red-600 to-red-700',
    text: 'text-white',
    Icon: XCircle,
  },
};

const Stat = ({ label, value, accent }) => (
  <div className="flex justify-between items-baseline py-1.5 border-b border-white/10 last:border-0">
    <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
    <span className={`text-lg font-bold ${accent || ''}`}>{value}</span>
  </div>
);

export const EngineHealthWidget = ({ token, onOpenAudit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/content-engine/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const reconcile = async () => {
    setReconciling(true);
    try {
      await fetch(`${API_URL}/api/admin/content-engine/reconcile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) {}
    finally {
      // Result may take ~60s/story; poll until done
      const poll = setInterval(async () => {
        await load();
        setReconciling(false);
        clearInterval(poll);
      }, 6000);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-gray-100 to-gray-200">
        <CardContent className="p-5 flex items-center gap-2 text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading engine health…
        </CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="bg-red-50 border-red-100">
        <CardContent className="p-5 text-sm text-red-600">{error || 'Unavailable'}</CardContent>
      </Card>
    );
  }

  const meta = LEVEL_META[data.level] || LEVEL_META.amber;
  const Icon = meta.Icon;

  return (
    <Card className={`overflow-hidden ${meta.bg} ${meta.text}`} data-testid="engine-health-widget">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] opacity-90">
              <Activity className="w-3 h-3" /> Story Engine Health
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Icon className="w-5 h-5" />
              <h3 className="text-xl font-bold leading-tight">{meta.label}</h3>
              <span className="ml-1 text-xs opacity-80">({data.percent_complete}%)</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onOpenAudit && (
              <button
                type="button"
                data-testid="open-audit-table"
                onClick={onOpenAudit}
                title="Open audit table"
                className="p-1.5 rounded-full hover:bg-white/15 transition"
              >
                <ListChecks className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              data-testid="run-reconciler"
              onClick={reconcile}
              disabled={reconciling}
              title="Run reconciler now"
              className="p-1.5 rounded-full hover:bg-white/15 transition disabled:opacity-60"
            >
              {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Stat label="Total stories" value={data.total_stories} />
        <Stat label="Approved" value={data.approved_stories} />
        <Stat label="Transformed" value={data.transformed_stories} />
        <Stat label="Missing outputs" value={data.stories_missing_outputs}
              accent={data.stories_missing_outputs > 0 ? 'text-yellow-200' : ''} />
        <Stat label="With errors" value={data.stories_with_errors}
              accent={data.stories_with_errors > 0 ? 'text-red-200' : ''} />

        <div className="text-[10px] opacity-70 mt-3">
          Engine version {data.engine_version} · checked {new Date(data.checked_at).toLocaleString()}
        </div>
        {reconciling && (
          <div className="text-xs mt-2 opacity-90 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Reconciling… outputs regenerate at ~60s per story.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EngineHealthWidget;
